import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  completeMercadoPagoConnection,
  rememberMercadoPagoConnectError,
  rememberMercadoPagoConnectSuccess,
} from "../../services/supabase/mercadoPagoConnectService";

const styles = {
  page: {
    minHeight: "56vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 0",
  },
  card: {
    width: "100%",
    maxWidth: "720px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe2ea",
    borderRadius: "18px",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
    padding: "28px",
    display: "grid",
    gap: "12px",
  },
  eyebrow: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "30px",
    lineHeight: 1.1,
  },
  copy: {
    margin: 0,
    color: "#475569",
    fontSize: "15px",
    lineHeight: 1.65,
  },
  status: {
    borderRadius: "14px",
    padding: "14px 16px",
    fontWeight: 700,
  },
  statusOk: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  statusError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  footer: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.55,
  },
};

function MercadoPagoConnectCallback() {
  const location = useLocation();
  const handledRef = useRef(false);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState(
    "Estamos cerrando la autorizacion con Mercado Pago para esta sucursal."
  );

  const callbackParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    return {
      code: String(searchParams.get("code") || "").trim(),
      state: String(searchParams.get("state") || "").trim(),
      errorCode: String(
        searchParams.get("error") || searchParams.get("error_code") || ""
      ).trim(),
      errorDescription: String(
        searchParams.get("error_description") ||
          searchParams.get("errorDescription") ||
          ""
      ).trim(),
      branchId: String(searchParams.get("branchId") || "").trim(),
    };
  }, [location.search]);

  useEffect(() => {
    if (handledRef.current) {
      return undefined;
    }

    handledRef.current = true;
    let cancelled = false;

    const finalizeConnection = async () => {
      try {
        const result = await completeMercadoPagoConnection(callbackParams);

        if (cancelled) {
          return;
        }

        rememberMercadoPagoConnectSuccess(result);
        setStatus("success");
        setMessage(
          result.message ||
            "La cuenta de Mercado Pago quedo conectada a MozzoApp para cobrar en Menu QR y kiosk."
        );
      } catch (error) {
        if (cancelled) {
          return;
        }

        const nextMessage =
          error.message ||
          "No se pudo completar la conexion con Mercado Pago para esta sucursal.";

        rememberMercadoPagoConnectError(nextMessage, callbackParams.branchId);
        setStatus("error");
        setMessage(nextMessage);
      }
    };

    finalizeConnection();

    return () => {
      cancelled = true;
    };
  }, [callbackParams]);

  return (
    <div style={styles.page}>
      <section style={styles.card}>
        <span style={styles.eyebrow}>Cobros kiosk · Mercado Pago</span>
        <h1 style={styles.title}>Conectando la cuenta digital</h1>
        <p style={styles.copy}>
          Estamos terminando la vinculacion para que esta sucursal pueda emitir
          cobros digitales desde Menu QR y kiosk.
        </p>
        <div
          style={{
            ...styles.status,
            ...(status === "error" ? styles.statusError : styles.statusOk),
          }}
        >
          {status === "loading" ? "Conectando..." : message}
        </div>
        <p style={styles.footer}>
          {status === "success"
            ? "Listo. Puedes cerrar esta ventana y volver a MozzoApp. La sucursal terminara de reflejar la vinculacion automaticamente."
            : status === "error"
              ? "No pudimos cerrar la vinculacion desde esta ventana. Vuelve a MozzoApp y revisa el mensaje del estado para reintentar."
              : "Estamos cerrando la autorizacion en MozzoApp. Cuando termine, puedes volver a la compu o a la app principal."}
        </p>
      </section>
    </div>
  );
}

export default MercadoPagoConnectCallback;
